import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, User, Settings, LogOut, Ticket } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config'

interface UserProfile {
  credit_balance?: number;
  letter_sent?: number;
}

const Navigation = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (location.pathname === '/dashboard' && token) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            setProfile(data.user);
          }
        } catch (error) {
          console.error('Error fetching profile:', error);
        }
      }
    };

    fetchProfile();
  }, [token, location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const RedeemButton = () => (
    <Link 
      to="/redeem-coupon" 
      className="flex items-center space-x-2 text-gray-700 hover:text-gray-900"
    >
      <Ticket className="h-5 w-5" />
      <span className="text-sm font-medium">Redeem Coupon</span>
    </Link>
  );

  // Logged out navigation
  if (!user) {
    return (
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="/images/logo/LOGO_1.png" 
                alt="Logo" 
                className="h-8 w-auto"
              />
            </Link>
            <div className="flex items-center space-x-4">
              <RedeemButton />
              <Link 
                to="/login" 
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Login
              </Link>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  // Logged in navigation
  return (
    <nav className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left side */}
          <div className="flex items-center space-x-8">
            <Link to="/" className="flex items-center">
              <img 
                src="/images/logo/LOGO_1.png" 
                alt="Logo" 
                className="h-8 w-auto"
              />
            </Link>
            <span className="text-gray-700">
              Hi <span className="font-semibold">{user.name}</span>, welcome to your private space
            </span>
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-6">
            {/* Show credits, letters, and redeem button only on dashboard */}
            {location.pathname === '/dashboard' && (
              <>
                {profile && (
                  <>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-700">
                        {profile.credit_balance} credits
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Mail className="h-5 w-5 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">
                        {profile.letter_sent} sent
                      </span>
                    </div>
                  </>
                )}
                <RedeemButton />
              </>
            )}

            {/* User dropdown */}
            <div className="relative group">
              <button className="flex items-center space-x-2 text-gray-700 hover:text-gray-900">
                <User className="h-5 w-5" />
              </button>
              <div className="absolute right-0 w-48 mt-2 py-2 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ease-in-out z-50">
                <Link 
                  to="/dashboard" 
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <User className="h-4 w-4 mr-2" />
                  Private Space
                </Link>

                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;